import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { Level, LevelType } from './types/Level';
import { loadLevels, updateLevel, deleteLevel, exportLevelsAsJSON, changeLevelNumber, addLevelWithId } from './utils/storage';
import LevelForm from './components/LevelForm';
import LevelCard from './components/LevelCard';
import Login from './components/Login';
import SettingsManager from './components/SettingsManager';
import StatisticsTab from './components/StatisticsTab';
import ActiveUsersWidget from './components/ActiveUsersWidget';
import UserManagement from './components/UserManagement';
import Highscores from './components/Highscores';
import LevelStudio from './components/studio/LevelStudio';
import { removeDraft } from './studio/queueStore';
import firebaseService from './services/firebaseService';
import KeepMounted from './components/KeepMounted';
import { useAuth } from './contexts/AuthContext';
import { signOutUser } from './services/authService';
import { Plus, Download, BarChart3, Gamepad2, Loader2, Wifi, WifiOff, ArrowUp, ArrowDown, LogOut, User, ChevronLeft, ChevronRight, List, Search, AlertTriangle, Edit2, Settings, ArrowLeftRight, Users, FileArchive, Wand2 } from 'lucide-react';
import roadilIcon from './roadil-icon.png';
import { LevelAnalytics } from './types/Analytics';
import { analyticsService } from './services/analyticsService';

type Tab = 'overview' | 'create' | 'manage' | 'summary' | 'settings' | 'statistics' | 'users' | 'highscores' | 'suggestions';
type SortBy = 'index' | 'category' | 'difficulty' | 'successRate' | 'imageSize';
type SortOrder = 'asc' | 'desc';

function App() {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [levels, setLevels] = useState<Level[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('index');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [sentenceSearchTerm, setSentenceSearchTerm] = useState('');
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapLevel1, setSwapLevel1] = useState('');
  const [swapLevel2, setSwapLevel2] = useState('');
  const [filterLevelType, setFilterLevelType] = useState<LevelType | 'all'>('all');
  const [levelAnalytics, setLevelAnalytics] = useState<Map<string, LevelAnalytics>>(new Map());
  const [suggestedWords, setSuggestedWords] = useState<string>('');
  const [prefilledLevelData, setPrefilledLevelData] = useState<Partial<Level> | null>(null);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showLinksDropdown, setShowLinksDropdown] = useState(false);

  // Sync URL with active tab and editing state on mount
  useEffect(() => {
    const path = location.pathname.slice(1) || 'overview'; // Remove leading slash
    const params = new URLSearchParams(location.search);
    const editLevelId = params.get('edit');

    if (['overview', 'create', 'manage', 'summary', 'settings', 'statistics', 'users', 'highscores', 'suggestions'].includes(path)) {
      setActiveTab(path as Tab);
    }

    // Restore editing state if edit parameter exists
    if (path === 'users' && editLevelId) {
      // User edit mode
      setSelectedUserId(editLevelId);
      setActiveTab('users');
    } else if (editLevelId && levels.length > 0) {
      // Level edit mode
      const levelToEdit = levels.find(l => l.id === editLevelId);
      if (levelToEdit) {
        setEditingLevel(levelToEdit);
        setActiveTab('create');
      }
    }
  }, [location.pathname, location.search, levels]);

  // Update URL when tab changes
  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    // Clear editing state when changing tabs (unless staying on create)
    if (tab !== 'create' && editingLevel) {
      setEditingLevel(null);
    }
    navigate(`/${tab}`);
  };

  useEffect(() => {
    if (currentUser) {
      loadLevelsData();
      loadAnalyticsData();
    }
  }, [currentUser]);

  const loadAnalyticsData = async () => {
    try {
      const analytics = await analyticsService.getAllLevelAnalytics();
      setLevelAnalytics(analytics);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Show loading spinner while checking auth status
  if (authLoading) {
    return (
      <div className="app">
        <div className="auth-loading">
          <Loader2 size={48} className="animate-spin" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!currentUser) {
    return <Login />;
  }

  const loadLevelsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedLevels = await loadLevels();

      // Fetch image sizes for levels with images
      const levelsWithSizes = await Promise.all(
        loadedLevels.map(async (level) => {
          if (level.image && !level.imageSize) {
            try {
              const { getStorage, ref, getMetadata } = await import('firebase/storage');
              const storage = getStorage();
              const imageRef = ref(storage, level.image);
              const metadata = await getMetadata(imageRef);
              return { ...level, imageSize: metadata.size };
            } catch (error) {
              console.error(`Failed to fetch size for ${level.image}: `, error);
              return level;
            }
          }
          return level;
        })
      );

      setLevels(levelsWithSizes);
      setIsOnline(true);
    } catch (error) {
      console.error('Failed to load levels:', error);
      setError('Failed to load levels. Check your connection.');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const hebrewEndingLetters = ['ם', 'ן', 'ף', 'ץ', 'ך'];

  const hasHebrewEndingLetters = (letters: string[] | undefined): boolean => {
    if (!letters || letters.length === 0) return false;
    return letters.some(letter => hebrewEndingLetters.includes(letter));
  };

  const getWarnings = () => {
    const warnings: { message: string; level: Level }[] = [];

    levels.forEach(level => {
      if (hasHebrewEndingLetters(level.keyLetters)) {
        const endingLettersFound = level.keyLetters!.filter(letter => hebrewEndingLetters.includes(letter));
        warnings.push({
          message: `Level ${level.id} contains Hebrew ending letters in keyboard: ${endingLettersFound.join(', ')} `,
          level
        });
      }

      // Check for empty cells in WordSearch grids
      if (level.levelType === LevelType.WordSearch && level.wordSearchGrid) {
        const grid = level.wordSearchGrid.grid;
        let emptyCellCount = 0;

        for (let row = 0; row < grid.length; row++) {
          for (let col = 0; col < grid[row].length; col++) {
            if (!grid[row][col] || grid[row][col].trim() === '') {
              emptyCellCount++;
            }
          }
        }

        if (emptyCellCount > 0) {
          warnings.push({
            message: `Level ${level.id} has ${emptyCellCount} empty cell${emptyCellCount > 1 ? 's' : ''} in the word search grid`,
            level
          });
        }

        // Check for incorrect direction words (left-to-right or wrong diagonals)
        const incorrectWords = level.wordSearchGrid.wordPositions.filter(
          wp => (wp as any).direction === 'horizontal' ||
            (wp as any).direction === 'diagonal-down-right' ||
            (wp as any).direction === 'diagonal-up-right'
        );
        if (incorrectWords.length > 0) {
          warnings.push({
            message: `Level ${level.id} has ${incorrectWords.length} word${incorrectWords.length > 1 ? 's' : ''} with incorrect direction(left - to - right or wrong diagonal) that need to be fixed`,
            level
          });
        }
      }
    });

    return warnings;
  };

  const handleCreateLevel = async (levelData: Omit<Level, 'id'>, levelNumber: string) => {
    try {
      setLoading(true);
      // Check if level number already exists
      const existingLevel = levels.find(level => level.id === levelNumber);
      if (existingLevel) {
        setError(`Level ${levelNumber} already exists.Please choose a different number.`);
        return;
      }

      // Create level with specific ID.
      // A Level Studio draft uses the strict writer: it must reach Firestore (no local fallback)
      // and never overwrite, so the draft is only removed once the level really exists.
      const newLevel = editingDraftId
        ? await firebaseService.createLevelIfAbsent(levelNumber, levelData)
        : await addLevelWithId(levelNumber, levelData);
      setLevels(prev => [newLevel, ...prev]);
      setPrefilledLevelData(null);
      if (editingDraftId) {
        removeDraft(editingDraftId);
        setEditingDraftId(null);
        handleTabChange('suggestions');
      } else {
        setActiveTab('manage');
      }
      setIsOnline(true);
    } catch (error) {
      console.error('Failed to create level:', error);
      setError('Failed to create level. Check your connection.');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLevel = (level: Level) => {
    setEditingLevel(level);
    setActiveTab('create');
    // Update URL to include editing state
    navigate(`/create?edit=${level.id}`);
  };

  const handleUpdateLevel = async (levelData: Omit<Level, 'id'>, levelNumber: string, originalId?: string) => {
    if (editingLevel && originalId) {
      try {
        setLoading(true);

        // Check if level number changed
        if (levelNumber !== originalId) {
          // Check if new level number already exists
          const existingLevel = levels.find(level => level.id === levelNumber && level.id !== originalId);
          if (existingLevel) {
            setError(`Level ${levelNumber} already exists.Please choose a different number.`);
            return;
          }

          // Recreate document with new ID
          const updatedLevel = await changeLevelNumber(originalId, levelNumber, levelData);
          setLevels(prev => prev.map(level =>
            level.id === originalId ? updatedLevel : level
          ));
        } else {
          // Normal update with same ID
          const updatedLevel = await updateLevel(originalId, levelData);
          if (updatedLevel) {
            setLevels(prev => prev.map(level =>
              level.id === originalId ? updatedLevel : level
            ));
          }
        }

        setEditingLevel(null);
        handleTabChange('manage');
        // Clear URL query params
        navigate('/manage');
        setIsOnline(true);
      } catch (error) {
        console.error('Failed to update level:', error);
        setError('Failed to update level. Check your connection.');
        setIsOnline(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteLevel = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this level?')) {
      try {
        setLoading(true);
        const success = await deleteLevel(id);
        if (success) {
          setLevels(prev => prev.filter(level => level.id !== id));
          setIsOnline(true);
        }
      } catch (error) {
        console.error('Failed to delete level:', error);
        setError('Failed to delete level. Check your connection.');
        setIsOnline(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportLevels = async () => {
    try {
      setLoading(true);
      const jsonData = await exportLevelsAsJSON();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `road-il-levels-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsOnline(true);
    } catch (error) {
      console.error('Failed to export levels:', error);
      setError('Failed to export levels. Check your connection.');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAllImages = async () => {
    try {
      setLoading(true);
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Get levels with images
      const levelsWithImages = levels.filter(level => level.image);

      if (levelsWithImages.length === 0) {
        alert('No images found in levels');
        return;
      }

      // Fetch and add each image to the zip
      const { getStorage, ref, getBytes } = await import('firebase/storage');
      const storage = getStorage();

      for (const level of levelsWithImages) {
        try {
          if (!level.image) continue; // Extra safety check

          const imageRef = ref(storage, level.image);

          // Get the image as bytes directly from Firebase Storage (avoids CORS)
          const bytes = await getBytes(imageRef);
          const blob = new Blob([bytes]);

          // Get file extension from original filename
          const extension = level.image.split('.').pop() || 'png';
          const filename = `${level.id}.${extension} `;

          // Add to zip
          zip.file(filename, blob);
        } catch (error) {
          console.error(`Failed to download image for level ${level.id}: `, error);
        }
      }

      // Generate and download the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `road-il-level-images-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setIsOnline(true);
    } catch (error) {
      console.error('Failed to download images:', error);
      setError('Failed to download images. Check your connection.');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingLevel(null);
    setPrefilledLevelData(null);
    setEditingDraftId(null);
    setActiveTab('manage');
  };

  const handleSwapLevels = async () => {
    if (!swapLevel1 || !swapLevel2) {
      setError('Please select both levels to swap');
      return;
    }

    if (swapLevel1 === swapLevel2) {
      setError('Please select two different levels');
      return;
    }

    const level1 = levels.find(l => l.id === swapLevel1);
    const level2 = levels.find(l => l.id === swapLevel2);

    if (!level1 || !level2) {
      setError('One or both levels not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Generate a temporary ID that won't conflict
      const tempId = `temp_${Date.now()} `;

      // Step 1: Move level1 to temp location
      const { id: _, ...level1Data } = level1;
      await changeLevelNumber(swapLevel1, tempId, level1Data);

      // Step 2: Move level2 to level1's position
      const { id: __, ...level2Data } = level2;
      await changeLevelNumber(swapLevel2, swapLevel1, level2Data);

      // Step 3: Move temp (original level1) to level2's position
      await changeLevelNumber(tempId, swapLevel2, level1Data);

      // Update local state
      setLevels(prev => prev.map(level => {
        if (level.id === swapLevel1) return { ...level2, id: swapLevel1 };
        if (level.id === swapLevel2) return { ...level1, id: swapLevel2 };
        return level;
      }));

      // Close modal and reset
      setShowSwapModal(false);
      setSwapLevel1('');
      setSwapLevel2('');
      setIsOnline(true);
    } catch (error) {
      console.error('Failed to swap levels:', error);
      setError('Failed to swap levels. Check your connection.');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    handleTabChange('users');
  };

  const getNavigationLevels = () => {
    if (!editingLevel) return { prevLevel: null, nextLevel: null };

    const sortedLevels = getSortedLevels();
    const currentIndex = sortedLevels.findIndex(level => level.id === editingLevel.id);

    return {
      prevLevel: currentIndex > 0 ? sortedLevels[currentIndex - 1] : null,
      nextLevel: currentIndex < sortedLevels.length - 1 ? sortedLevels[currentIndex + 1] : null
    };
  };

  const handleNavigateToLevel = (level: Level) => {
    setEditingLevel(level);
  };

  const getStats = () => {
    const totalLevels = levels.length;
    const levelsByType = levels.reduce((acc, level) => {
      acc[level.levelType] = (acc[level.levelType] || 0) + 1;
      return acc;
    }, {} as Record<LevelType, number>);

    const avgDifficulty = levels.length > 0
      ? (levels.reduce((sum, level) => sum + level.difficulty, 0) / levels.length).toFixed(1)
      : '0';

    return { totalLevels, levelsByType, avgDifficulty };
  };

  const getSortedLevels = () => {
    return [...levels].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'index':
          comparison = parseInt(a.id) - parseInt(b.id);
          break;
        case 'category':
          // Sort by level type, not category text
          const typeA = a.levelType || '';
          const typeB = b.levelType || '';
          comparison = typeA.localeCompare(typeB);
          break;
        case 'difficulty':
          comparison = a.difficulty - b.difficulty;
          break;
        case 'successRate':
          const analyticsA = levelAnalytics.get(a.id);
          const analyticsB = levelAnalytics.get(b.id);
          const rateA = analyticsA?.successRate || 0;
          const rateB = analyticsB?.successRate || 0;
          comparison = rateA - rateB;
          break;
        case 'imageSize':
          const sizeA = a.imageSize || 0;
          const sizeB = b.imageSize || 0;
          comparison = sizeA - sizeB;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const handleSortChange = (newSortBy: SortBy) => {
    if (newSortBy === sortBy) {
      // Toggle sort order if same field is clicked
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with default order
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const renderOverview = () => {
    const { totalLevels, levelsByType, avgDifficulty } = getStats();

    return (
      <div>
        {/* Active Users Widget */}
        <div style={{ marginBottom: '2rem' }}>
          <ActiveUsersWidget onUserClick={handleUserClick} />
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{totalLevels}</div>
            <div className="stat-label">Total Levels</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{avgDifficulty}</div>
            <div className="stat-label">Avg Difficulty</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{Object.keys(levelsByType).length}</div>
            <div className="stat-label">Level Types</div>
          </div>
        </div>

        {Object.keys(levelsByType).length > 0 && (
          <div className="form-container">
            <h3>Levels by Type</h3>
            <div className="stats-grid">
              {Object.entries(levelsByType).map(([type, count]) => (
                <div key={type} className="stat-card">
                  <div className="stat-number">{count}</div>
                  <div className="stat-label">{type.replace(/([A-Z])/g, ' $1').trim()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalLevels > 0 && (
          <div className="export-section">
            <h3>Export Levels</h3>
            <p>Export all your levels as a JSON file that can be imported into your Unity project.</p>
            <button onClick={handleExportLevels} className="btn btn-primary">
              <Download size={16} />
              Export as JSON
            </button>
          </div>
        )}

        {totalLevels === 0 && (
          <div className="empty-state">
            <Gamepad2 size={64} />
            <h3>No levels created yet</h3>
            <p>Start by creating your first level!</p>
            <button
              onClick={() => {
                setEditingLevel(null);
                setActiveTab('create');
              }}
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
            >
              <Plus size={16} />
              Create First Level
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCreateTab = () => {
    const { prevLevel, nextLevel } = getNavigationLevels();

    return (
      <div>
        {/* Navigation Arrows */}
        {editingLevel && (
          <>
            {nextLevel && (
              <button
                className="level-nav-arrow level-nav-left"
                onClick={() => handleNavigateToLevel(nextLevel)}
                title={`Edit Level ${nextLevel.id} `}
              >
                <ChevronLeft size={24} />
                <span>Level {nextLevel.id}</span>
              </button>
            )}
            {prevLevel && (
              <button
                className="level-nav-arrow level-nav-right"
                onClick={() => handleNavigateToLevel(prevLevel)}
                title={`Edit Level ${prevLevel.id} `}
              >
                <span>Level {prevLevel.id}</span>
                <ChevronRight size={24} />
              </button>
            )}
          </>
        )}

        <h2>{editingLevel ? 'Edit Level' : 'Create New Level'}</h2>
        <LevelForm
          onSubmit={editingLevel ? handleUpdateLevel : handleCreateLevel}
          initialData={(editingLevel || prefilledLevelData) as Level | undefined}
          existingLevels={levels}
          onCancel={editingLevel || prefilledLevelData ? handleCancelEdit : undefined}
          suggestedWords={suggestedWords}
        />
      </div>
    );
  };

  const renderManageTab = () => {
    const sortedLevels = getSortedLevels();

    // Apply level type filter
    const filteredLevels = filterLevelType === 'all'
      ? sortedLevels
      : sortedLevels.filter(level => level.levelType === filterLevelType);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2>Manage Levels ({filterLevelType === 'all' ? levels.length : `${filteredLevels.length} /${levels.length}`})</h2 >
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleDownloadAllImages}
              className="btn btn-secondary"
              disabled={levels.filter(l => l.image).length === 0}
              title="Download all level images as a ZIP file"
            >
              <FileArchive size={16} />
              Download Images
            </button>
            <button
              onClick={() => setShowSwapModal(true)}
              className="btn btn-secondary"
              disabled={levels.length < 2}
            >
              <ArrowLeftRight size={16} />
              Swap Levels
            </button>
            <button onClick={() => {
              setEditingLevel(null);
              setActiveTab('create');
            }} className="btn btn-primary">
              <Plus size={16} />
              Add New Level
            </button>
          </div>
        </div >

        {
          levels.length === 0 ? (
            <div className="empty-state">
              <Gamepad2 size={64} />
              <h3>No levels found</h3>
              <p>Create your first level to get started!</p>
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '1.5rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontWeight: 'bold', color: '#6b7280' }}>Sort by:</span>

                <button
                  onClick={() => handleSortChange('index')}
                  className={`btn ${sortBy === 'index' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Index
                  {sortBy === 'index' && (
                    sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                  )}
                </button>

                <button
                  onClick={() => handleSortChange('category')}
                  className={`btn ${sortBy === 'category' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Level Type
                  {sortBy === 'category' && (
                    sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                  )}
                </button>

                <button
                  onClick={() => handleSortChange('difficulty')}
                  className={`btn ${sortBy === 'difficulty' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Difficulty
                  {sortBy === 'difficulty' && (
                    sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                  )}
                </button>

                <button
                  onClick={() => handleSortChange('successRate')}
                  className={`btn ${sortBy === 'successRate' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Success Rate
                  {sortBy === 'successRate' && (
                    sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                  )}
                </button>

                <button
                  onClick={() => handleSortChange('imageSize')}
                  className={`btn ${sortBy === 'imageSize' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  Image Size
                  {sortBy === 'imageSize' && (
                    sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                  )}
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: 'bold',
                  color: '#6b7280',
                  marginBottom: '0.5rem'
                }}>
                  Filter by Level Type:
                </label>
                <select
                  value={filterLevelType}
                  onChange={(e) => setFilterLevelType(e.target.value as LevelType | 'all')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '200px'
                  }}
                >
                  <option value="all">All Level Types</option>
                  <option value={LevelType.CategoryWords}>Category Words</option>
                  <option value={LevelType.KnownSentence}>Known Sentence</option>
                  <option value={LevelType.WordSearch}>Word Search</option>
                  <option value={LevelType.WhatsInThePicture}>What's in the Picture</option>
                  <option value={LevelType.WhereIsThisLocated}>Where is This Located</option>
                  <option value={LevelType.WhatsTheSong}>What's the Song</option>
                </select>
              </div>

              <div className="levels-grid">
                {filteredLevels.map((level) => (
                  <LevelCard
                    key={level.id}
                    level={level}
                    onEdit={handleEditLevel}
                    onDelete={handleDeleteLevel}
                    analytics={levelAnalytics.get(level.id)}
                  />
                ))}
              </div>
            </>
          )
        }

        {/* Swap Levels Modal */}
        {
          showSwapModal && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => {
                setShowSwapModal(false);
                setSwapLevel1('');
                setSwapLevel2('');
              }}
            >
              <div
                style={{
                  backgroundColor: 'white',
                  padding: '2rem',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Swap Level Indexes</h3>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                  Select two levels to swap their index numbers. The content of each level will move to the other level's position.
                </p>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    First Level
                  </label>
                  <select
                    value={swapLevel1}
                    onChange={(e) => setSwapLevel1(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a level...</option>
                    {levels
                      .sort((a, b) => parseInt(a.id) - parseInt(b.id))
                      .map(level => (
                        <option key={level.id} value={level.id}>
                          Level {level.id} - {level.levelType}
                        </option>
                      ))}
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Second Level
                  </label>
                  <select
                    value={swapLevel2}
                    onChange={(e) => setSwapLevel2(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a level...</option>
                    {levels
                      .sort((a, b) => parseInt(a.id) - parseInt(b.id))
                      .map(level => (
                        <option key={level.id} value={level.id}>
                          Level {level.id} - {level.levelType}
                        </option>
                      ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowSwapModal(false);
                      setSwapLevel1('');
                      setSwapLevel2('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSwapLevels}
                    className="btn btn-primary"
                    disabled={!swapLevel1 || !swapLevel2}
                  >
                    <ArrowLeftRight size={16} />
                    Swap Levels
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div >
    );
  };

  const renderSummaryTab = () => {
    // Extract categories with their words and sentences grouped by type
    const categoriesWithWords = new Map<string, string[]>();
    const sentencesByType = new Map<string, Set<string>>();

    levels.forEach(level => {
      // Collect categories with their words
      if (level.category && level.category.trim()) {
        const category = level.category.trim();
        if (!categoriesWithWords.has(category)) {
          categoriesWithWords.set(category, []);
        }
        // Add words from this level to the category
        const existingWords = categoriesWithWords.get(category)!;

        // For WordSearch levels with new grid structure
        if (level.levelType === LevelType.WordSearch && level.wordSearchGrid?.wordPositions) {
          level.wordSearchGrid.wordPositions.forEach(wordPos => {
            const word = wordPos.word.trim();
            if (word && !existingWords.includes(word)) {
              existingWords.push(word);
            }
          });
        }
        // For other levels or legacy WordSearch levels
        else if (level.words && level.words.length > 0) {
          level.words.forEach(word => {
            if (word.trim() && !existingWords.includes(word.trim())) {
              existingWords.push(word.trim());
            }
          });
        }
      }

      // Collect sentences grouped by level type
      if (level.sentence && level.sentence.trim()) {
        if (!sentencesByType.has(level.levelType)) {
          sentencesByType.set(level.levelType, new Set());
        }
        sentencesByType.get(level.levelType)!.add(level.sentence.trim());
      }
    });

    // Sort categories and their words alphabetically
    let sortedCategoriesWithWords = Array.from(categoriesWithWords.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, words]) => [category, words.sort()] as [string, string[]]);

    // Filter categories based on search term
    if (categorySearchTerm.trim()) {
      const searchLower = categorySearchTerm.toLowerCase();
      sortedCategoriesWithWords = sortedCategoriesWithWords.filter(([category, words]) => {
        // Search in category name
        if (category.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in words within category
        return words.some(word => word.toLowerCase().includes(searchLower));
      }).map(([category, words]) => {
        // If searching, also filter words within each category
        const filteredWords = words.filter(word =>
          word.toLowerCase().includes(searchLower) || category.toLowerCase().includes(searchLower)
        );
        return [category, filteredWords] as [string, string[]];
      });
    }

    const totalWords = Array.from(categoriesWithWords.values())
      .reduce((total, words) => total + words.length, 0);

    const filteredWordsCount = sortedCategoriesWithWords
      .reduce((total, [, words]) => total + words.length, 0);

    // Sort level types and their sentences
    let sortedSentencesByType = Array.from(sentencesByType.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([levelType, sentences]) => [levelType, Array.from(sentences).sort()] as [string, string[]]);

    // Filter sentences based on search term
    if (sentenceSearchTerm.trim()) {
      const searchLower = sentenceSearchTerm.toLowerCase();
      sortedSentencesByType = sortedSentencesByType
        .map(([levelType, sentences]) => {
          const filteredSentences = sentences.filter(sentence =>
            sentence.toLowerCase().includes(searchLower)
          );
          return [levelType, filteredSentences] as [string, string[]];
        })
        .filter(([, sentences]) => sentences.length > 0);
    }

    const totalSentences = Array.from(sentencesByType.values())
      .reduce((total, sentences) => total + sentences.size, 0);

    const filteredSentencesCount = sortedSentencesByType
      .reduce((total, [, sentences]) => total + sentences.length, 0);

    return (
      <div>
        <h2>Content Summary</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
          {/* Categories Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#374151' }}>
                Categories ({categorySearchTerm.trim() ? `${filteredWordsCount}/${totalWords}` : totalWords} words)
              </h3>
            </div>

            {/* Search Input for Categories */}
            <div style={{
              position: 'relative',
              marginBottom: '1rem'
            }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }}
              />
              <input
                type="text"
                placeholder="Search categories or words..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem 0.6rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {sortedCategoriesWithWords.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                {categorySearchTerm.trim() ? 'No matching categories or words found' : 'No categories found'}
              </p>
            ) : (
              <div style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                maxHeight: '60vh',
                overflowY: 'auto'
              }}>
                {sortedCategoriesWithWords.map(([category, words], categoryIndex) => (
                  <div key={category} style={{ marginBottom: categoryIndex < sortedCategoriesWithWords.length - 1 ? '1.5rem' : '0' }}>
                    {/* Category Header */}
                    <div style={{
                      background: '#f8fafc',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{
                        margin: 0,
                        color: '#475569',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}>
                        {category} ({words.length})
                      </h4>
                    </div>

                    {/* Words for this category */}
                    {words.length > 0 ? (
                      words.map((word, wordIndex) => (
                        <div
                          key={word}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderBottom: wordIndex < words.length - 1 ? '1px solid #f1f5f9' : 'none',
                            color: '#374151',
                            lineHeight: '1.4',
                            fontSize: '0.9rem'
                          }}
                        >
                          {word}
                        </div>
                      ))
                    ) : (
                      <div style={{
                        padding: '0.4rem 0.75rem',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        fontSize: '0.9rem'
                      }}>
                        No matching words
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sentences Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#374151' }}>
                Sentences ({sentenceSearchTerm.trim() ? `${filteredSentencesCount}/${totalSentences}` : totalSentences})
              </h3>
            </div>

            {/* Search Input for Sentences */}
            <div style={{
              position: 'relative',
              marginBottom: '1rem'
            }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }}
              />
              <input
                type="text"
                placeholder="Search sentences..."
                value={sentenceSearchTerm}
                onChange={(e) => setSentenceSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem 0.6rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {sortedSentencesByType.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                {sentenceSearchTerm.trim() ? 'No matching sentences found' : 'No sentences found'}
              </p>
            ) : (
              <div style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                maxHeight: '60vh',
                overflowY: 'auto'
              }}>
                {sortedSentencesByType.map(([levelType, sentences], typeIndex) => (
                  <div key={levelType} style={{ marginBottom: typeIndex < sortedSentencesByType.length - 1 ? '1.5rem' : '0' }}>
                    {/* Level Type Header */}
                    <div style={{
                      background: '#f8fafc',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{
                        margin: 0,
                        color: '#475569',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}>
                        {levelType} ({sentences.length})
                      </h4>
                    </div>

                    {/* Sentences for this type */}
                    {sentences.map((sentence, sentenceIndex) => (
                      <div
                        key={sentence}
                        style={{
                          padding: '0.4rem 0.75rem',
                          borderBottom: sentenceIndex < sentences.length - 1 ? '1px solid #f1f5f9' : 'none',
                          color: '#374151',
                          lineHeight: '1.4',
                          fontSize: '0.9rem'
                        }}
                      >
                        "{sentence}"
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <header className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1440px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ background: 'white', padding: '4px', borderRadius: '12px', display: 'flex', alignItems: 'center', border: '1px solid var(--glass-border)', boxShadow: 'var(--shadow-sm)' }}>
              <img src={roadilIcon} alt="RoadIL App Icon" width="36" height="36" style={{ borderRadius: '8px' }} />
            </div>
            <div>
              <h1 style={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em', margin: 0 }}>ROAD IL <span style={{ opacity: 0.8, fontWeight: 500 }}>DASHBOARD</span></h1>
              <p style={{ fontSize: '0.75rem', opacity: 0.9, fontWeight: 600, margin: 0 }}>Level Editor & Analytics</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {loading && <Loader2 size={24} className="animate-spin" color="white" />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {isOnline ? (
                <>
                  <Wifi size={20} style={{ color: 'white' }} />
                  <span style={{ color: 'white', fontSize: '0.9rem', fontWeight: 600 }}>Firebase Connected</span>
                </>
              ) : (
                <>
                  <WifiOff size={20} style={{ color: '#fee2e2' }} />
                  <span style={{ color: '#fee2e2', fontSize: '0.9rem', fontWeight: 600 }}>Offline Mode</span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingLeft: '1rem', borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
              <div className="links-dropdown-wrapper" style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowLinksDropdown(!showLinksDropdown)}
                  onBlur={() => setTimeout(() => setShowLinksDropdown(false), 200)}
                  className="header-link dropdown-toggle"
                  style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
                >
                  <List size={18} />
                  <span>External Links</span>
                </button>
                {showLinksDropdown && (
                  <div className="links-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', minWidth: '240px', zIndex: 1000, overflow: 'hidden' }}>

                    <div className="dropdown-section" style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
                      <h4 style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem', marginTop: 0 }}>Repositories</h4>
                      <a href="https://github.com/KupermanL/RoadIL-Unity" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#1e293b', textDecoration: 'none', fontSize: '0.9rem', marginBottom: '0.4rem', fontWeight: 600 }}>Github Unity App</a>
                      <a href="https://github.com/KupermanL/RoadIL-Admin-Panel" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#1e293b', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>Github Admin Panel</a>
                    </div>

                    <div className="dropdown-section" style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
                      <h4 style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem', marginTop: 0 }}>Consoles</h4>
                      <a href="https://console.firebase.google.com/u/0/project/thetower-2d922/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#1e293b', textDecoration: 'none', fontSize: '0.9rem', marginBottom: '0.4rem', fontWeight: 600 }}>Firebase Console</a>
                      <a href="https://play.google.com/console/u/0/developers/5901730052861619225/app/4975829903858658257/app-dashboard" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#1e293b', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>Google Play Console</a>
                    </div>

                    <div className="dropdown-section" style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
                      <h4 style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem', marginTop: 0 }}>Ads & Analytics</h4>
                      <a href="https://admob.google.com/v2/apps/2860150678/overview?pli=1" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#1e293b', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>Admob Android</a>
                    </div>

                    <div className="dropdown-section" style={{ padding: '0.75rem 1rem', background: '#f8fafc' }}>
                      <h4 style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem', marginTop: 0 }}>Stores</h4>
                      <a href="https://play.google.com/store/apps/details?id=com.kupigames.tower" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#16a34a', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>Google Play Store</a>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '1rem' }}>
                <User size={18} />
                <span style={{ fontSize: '0.9rem' }}>{currentUser.email}</span>
              </div>
              <button
                onClick={handleSignOut}
                className="logout-button"
                title="Sign Out"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="main-content">
          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '0.75rem',
              borderRadius: '8px',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                ×
              </button>
            </div>
          )}
        </div>

        <div className="main-content">
          {/* Warnings Section */}
          {getWarnings().length > 0 && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                color: '#92400e',
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
                <AlertTriangle size={20} />
                <span>Warnings ({getWarnings().length})</span>
              </div>
              {getWarnings().map((warning, index) => (
                <div
                  key={`warning-${warning.level.id}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    marginBottom: index < getWarnings().length - 1 ? '0.5rem' : '0',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #fbbf24'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flex: 1
                  }}>
                    <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
                    <span style={{ color: '#78350f', fontSize: '0.95rem' }}>
                      {warning.message}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      handleEditLevel(warning.level);
                      handleTabChange('create');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      marginLeft: '1rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#d97706';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f59e0b';
                    }}
                  >
                    <Edit2 size={14} />
                    Edit Level
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabChange('overview')}
            >
              <BarChart3 size={16} />
              Overview
            </button>
            <button
              className={`tab ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => {
                setEditingLevel(null);
                setPrefilledLevelData(null);
                setEditingDraftId(null);
                setActiveTab('create');
              }}
            >
              <Plus size={16} />
              Create Level
            </button>
            <button
              className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
              onClick={() => handleTabChange('manage')}
            >
              <Gamepad2 size={16} />
              Manage Levels
            </button>
            <button
              className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => handleTabChange('summary')}
            >
              <List size={16} />
              Summary
            </button>
            <button
              className={`tab ${activeTab === 'statistics' ? 'active' : ''}`}
              onClick={() => handleTabChange('statistics')}
            >
              <BarChart3 size={16} />
              Statistics
            </button>
            <button
              className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('settings')}
            >
              <Settings size={16} />
              Settings
            </button>
            <button
              className={`tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => handleTabChange('users')}
            >
              <Users size={16} />
              Users
            </button>
            <button
              className={`tab ${activeTab === 'highscores' ? 'active' : ''}`}
              onClick={() => handleTabChange('highscores')}
            >
              <BarChart3 size={16} />
              Highscores
            </button>
            <button
              className={`tab ${activeTab === 'suggestions' ? 'active' : ''}`}
              onClick={() => handleTabChange('suggestions')}
            >
              <Wand2 size={16} />
              Level Studio
            </button>
          </div>

          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'create' && renderCreateTab()}
          {activeTab === 'manage' && renderManageTab()}
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'statistics' && <StatisticsTab />}
          {activeTab === 'settings' && <SettingsManager />}
          {activeTab === 'users' && <UserManagement selectedUserId={selectedUserId} onUserSelected={() => setSelectedUserId(null)} />}
          {activeTab === 'highscores' && <Highscores />}
          <KeepMounted active={activeTab === 'suggestions'}>
            <LevelStudio
              visible={activeTab === 'suggestions'}
              levels={levels}
              onLevelCreated={(level) => setLevels(prev => [level, ...prev])}
              onEditDraft={(draft, prefill) => {
                setEditingLevel(null);
                setSuggestedWords(draft.levelType === LevelType.WordSearch && draft.words ? draft.words.join(', ') : '');
                setPrefilledLevelData(prefill);
                setEditingDraftId(draft.id);
                handleTabChange('create');
              }}
            />
          </KeepMounted>
        </div>
      </div >
    </div >
  );
}

export default App;